import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ClipboardCheck, AlertTriangle, CheckCircle2, ChevronRight, 
  FileText, Users, Filter, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

import API from '@/config/api';
// API imported from config;

// Form type display names
const FORM_NAMES = {
  enrollment: 'Enrollment',
  health_history: 'Health History',
  immunization: 'Immunization',
  physical_exam: 'Physical Exam'
};

const ChildFormsComplianceWidget = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'incomplete', 'complete'
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children`, getAuthHeaders());
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    total: children.length,
    complete: children.filter(c => c.forms_progress?.percentage === 100).length,
    incomplete: children.filter(c => (c.forms_progress?.percentage || 0) < 100).length,
    avgCompletion: children.length > 0 
      ? Math.round(children.reduce((sum, c) => sum + (c.forms_progress?.percentage || 0), 0) / children.length)
      : 0
  };

  // Filter children
  const filteredChildren = children.filter(child => {
    const progress = child.forms_progress?.percentage || 0;
    if (filter === 'incomplete') return progress < 100;
    if (filter === 'complete') return progress === 100;
    return true;
  });

  // Sort: incomplete first, then by percentage ascending
  const sortedChildren = [...filteredChildren].sort((a, b) => {
    const aProgress = a.forms_progress?.percentage || 0;
    const bProgress = b.forms_progress?.percentage || 0;
    return aProgress - bProgress;
  });

  const handleChildClick = (childId) => {
    navigate(`/children?highlight=${childId}`);
  };

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-8 bg-muted rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-clay" data-testid="child-forms-compliance-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-primary" />
            Children's DCF Forms Compliance
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <div className="text-2xl font-bold text-primary">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total Children</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
            <div className="text-2xl font-bold text-green-600">{stats.complete}</div>
            <div className="text-xs text-green-600">Fully Compliant</div>
          </div>
          <div className="p-3 bg-red-50 rounded-lg text-center border border-red-200">
            <div className="text-2xl font-bold text-red-600">{stats.incomplete}</div>
            <div className="text-xs text-red-600">Incomplete</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.avgCompletion}%</div>
            <div className="text-xs text-blue-600">Avg Completion</div>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Forms Completion</span>
            <span className="text-sm font-bold">{stats.avgCompletion}%</span>
          </div>
          <Progress 
            value={stats.avgCompletion} 
            className={`h-2 ${
              stats.avgCompletion >= 80 
                ? '[&>div]:bg-green-500' 
                : stats.avgCompletion >= 50 
                  ? '[&>div]:bg-amber-500'
                  : '[&>div]:bg-red-500'
            }`}
          />
        </div>

        {isExpanded && (
          <>
            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                size="sm"
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className="text-xs"
              >
                <Users className="w-3 h-3 mr-1" />
                All ({stats.total})
              </Button>
              <Button
                size="sm"
                variant={filter === 'incomplete' ? 'default' : 'outline'}
                onClick={() => setFilter('incomplete')}
                className={`text-xs ${filter !== 'incomplete' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'bg-red-600'}`}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                Incomplete ({stats.incomplete})
              </Button>
              <Button
                size="sm"
                variant={filter === 'complete' ? 'default' : 'outline'}
                onClick={() => setFilter('complete')}
                className={`text-xs ${filter !== 'complete' ? 'text-green-600 border-green-200 hover:bg-green-50' : 'bg-green-600'}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Complete ({stats.complete})
              </Button>
            </div>

            {/* Children List */}
            <ScrollArea className="h-[280px]">
              {sortedChildren.length > 0 ? (
                <div className="space-y-2">
                  {sortedChildren.map((child) => {
                    const progress = child.forms_progress || { completed: 0, total: 4, percentage: 0, missing: [] };
                    const isComplete = progress.percentage === 100;
                    
                    return (
                      <div
                        key={child.id}
                        className={`p-3 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                          isComplete 
                            ? 'bg-green-50 border border-green-200 hover:border-green-300' 
                            : progress.percentage >= 50
                              ? 'bg-amber-50 border border-amber-200 hover:border-amber-300'
                              : 'bg-red-50 border border-red-200 hover:border-red-300'
                        }`}
                        onClick={() => handleChildClick(child.id)}
                        data-testid={`compliance-child-${child.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                              isComplete 
                                ? 'bg-green-200 text-green-700' 
                                : progress.percentage >= 50
                                  ? 'bg-amber-200 text-amber-700'
                                  : 'bg-red-200 text-red-700'
                            }`}>
                              {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-sm">
                                {child.first_name} {child.last_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {child.classroom || 'No classroom'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className={`text-sm font-bold ${
                                isComplete ? 'text-green-600' : progress.percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {progress.completed}/{progress.total}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {progress.percentage}%
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                        
                        {/* Missing forms */}
                        {!isComplete && progress.missing?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dashed flex flex-wrap gap-1">
                            <span className="text-xs text-muted-foreground mr-1">Missing:</span>
                            {progress.missing.map(form => (
                              <Badge 
                                key={form} 
                                variant="outline" 
                                className="text-[10px] py-0 px-1.5 bg-white text-red-600 border-red-200"
                              >
                                {FORM_NAMES[form] || form}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No children match the selected filter</p>
                </div>
              )}
            </ScrollArea>

            {/* View All Button */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/children')}
              >
                <FileText className="w-4 h-4 mr-2" />
                View All Children
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ChildFormsComplianceWidget;
